import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { criarFormularioDeCredenciais, montarCredenciais } from 'src/app/components/auth-credentials-fields/auth-credentials-fields.component';
import { CredenciaisAutorizacao, DadosAprovarAcesso, LARGURA_MODAL_AUTORIZAR_ACESSO } from 'src/app/models/User/AprovarAcesso.model';
import { SessionService } from 'src/app/services/session/session.service';
import { AutorizacaoService } from 'src/app/services/permissao/autorizacao.service';
import { registrarFalhaDeAutorizacao } from 'src/app/services/permissao/registrar-falha-autorizacao';
import { DialogOndeAlterarAutorizacaoComponent } from '../dialog-onde-alterar-autorizacao/dialog-onde-alterar-autorizacao.component';

const ESPERA_ENTRE_TENTATIVAS_MS = 2000;
const ESPERA_APOS_ESGOTAR_MS = 60000;

// Os campos de credencial são os mesmos da tela de login (app-auth-credentials-fields)
@Component({
  selector: 'app-dialog-aprovar-acesso',
  templateUrl: './dialog-aprovar-acesso.component.html',
  styleUrls: ['./dialog-aprovar-acesso.component.scss']
})
export class DialogAprovarAcessoComponent implements OnInit {
  form: FormGroup;
  readonly email: string;

  // null enquanto carrega a lista de usuários
  unicoUsuario: boolean | null = null;
  // Falha ao saber se a conta tem um ou vários usuários: sem campos e sem autorização (falha fechada)
  erroAoCarregar = false;
  carregando = false;
  erro = '';
  // Regra 37: 5 senhas inválidas esgotam o modal; fechar e abrir de novo libera mais 5
  esgotado = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: DadosAprovarAcesso,
    private dialogRef: MatDialogRef<DialogAprovarAcessoComponent>,
    private dialog: MatDialog,
    private elementRef: ElementRef,
    private formBuilder: FormBuilder,
    private autorizacaoService: AutorizacaoService,
    private sessionService: SessionService,
    private translate: TranslateService
  ) {
    this.email = this.sessionService.getDetails().email;
    this.form = criarFormularioDeCredenciais(this.formBuilder);

    // Trocar entre administrador e usuário limpa também a mensagem de erro do modal
    this.form.get('authMode')?.valueChanges.subscribe(() => this.erro = '');
  }

  get funcionalidade(): string {
    return this.data.funcionalidade ?? '';
  }

  // Pop-up que ensina onde alterar a ação da funcionalidade que abriu este modal.
  // Abre com a largura e a altura que este modal tem na tela agora (a altura é mínima: cresce se o texto precisar).
  abrirAjuda(): void {
    const caixa = (this.elementRef.nativeElement as HTMLElement).closest('mat-dialog-container') as HTMLElement | null;

    this.dialog.open(DialogOndeAlterarAutorizacaoComponent, {
      width: caixa ? `${caixa.offsetWidth}px` : LARGURA_MODAL_AUTORIZAR_ACESSO,
      minHeight: caixa ? `${caixa.offsetHeight}px` : undefined,
      maxWidth: '95vw',
      autoFocus: false,
      data: { funcionalidade: this.funcionalidade }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.carregarOpcoes();
  }

  async carregarOpcoes(): Promise<void> {
    this.erroAoCarregar = false;
    this.unicoUsuario = null;

    try {
      // A empresa do usuário logado tem mais de um usuário ativo? (mesma regra do login)
      this.unicoUsuario = !(await this.autorizacaoService.getTemVariosUsuarios());
    } catch (erro) {
      registrarFalhaDeAutorizacao(erro, 'Falha ao carregar as opções da autorização');
      this.erroAoCarregar = true;
    }
  }

  // Com mais de um usuário, o autorizador escolhe entre administrador e usuário
  get mostrarOpcoes(): boolean {
    return this.unicoUsuario === false;
  }

  private get pedeUsuario(): boolean {
    return this.mostrarOpcoes && this.form.controls['authMode'].value === 'user';
  }

  async confirmar(): Promise<void> {
    if (this.carregando || this.esgotado || this.unicoUsuario === null) return;

    // Igual ao login: campo inválido não envia
    const senha = this.form.controls['password'];
    if (senha.invalid || (this.pedeUsuario && !this.form.controls['user'].value?.trim())) {
      senha.markAsTouched();
      senha.markAsDirty();
      return;
    }

    this.erro = '';
    this.carregando = true;

    try {
      const resultado = await this.data.validar(this.criarCredenciais());
      if (resultado.erro) {
        this.erro = resultado.erro;
        this.esgotado = !!resultado.tentativasEsgotadas;
        // Regra 37: a cada senha incorreta espera 2 segundos (botão desabilitado e carregando) antes de liberar nova tentativa
        if (!this.esgotado) await new Promise(resolver => setTimeout(resolver, ESPERA_ENTRE_TENTATIVAS_MS));
        // A API libera de novo 1 minuto depois do limite
        else setTimeout(() => this.esgotado = false, ESPERA_APOS_ESGOTAR_MS);
      } else {
        // Fecha devolvendo o token de autorização concedido pela API
        this.dialogRef.close(resultado.autorizacao);
      }
    } catch (erro) {
      registrarFalhaDeAutorizacao(erro, 'Falha inesperada ao validar a autorização');
      this.erro = this.translate.instant('accessApproval.validationError');
    }

    this.carregando = false;
  }

  cancelar(): void {
    this.dialogRef.close();
  }

  private criarCredenciais(): CredenciaisAutorizacao {
    return montarCredenciais(this.form, this.mostrarOpcoes);
  }
}
